import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, Crown, Sparkles, ArrowLeft, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { LEGAL_TEXT } from "@/lib/labels";

const BENEFITS = [
  "Accesso illimitato a tutte le lectiones dei 14 corsi + 3 percorsi speciali",
  "Prove del Maestro con grado di maestria",
  "Codex delle carte e punteggio di Maestria",
  "Impara dai grandi Maestri della filosofia",
];

export default function Subscribe() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(30);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [polling, setPolling] = useState(false);
  const [success, setSuccess] = useState(false);
  const pollRef = useRef(0);

  useEffect(() => {
    api.get("/plans").then((res) => { setPlans(res.data.plans); setTrialDays(res.data.trial_days); });
  }, []);

  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    setPolling(true);
    pollRef.current = 0;

    const poll = async () => {
      if (pollRef.current >= 15) {
        setPolling(false);
        toast.error("Verifica del pagamento scaduta. Ricarica la pagina.");
        return;
      }
      pollRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setPolling(false);
          setSuccess(true);
          toast.success("Abbonamento attivato!");
          return;
        }
        if (data.status === "expired") {
          setPolling(false);
          toast.error("Sessione di pagamento scaduta.");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const subscribe = async (planId) => {
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/checkout/session", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch {
      toast.error("Impossibile avviare il pagamento. Riprova.");
      setLoadingPlan(null);
    }
  };

  if (polling)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="py-40 flex flex-col items-center gap-4 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} />
          <p className="text-muted-foreground">Verifica del pagamento in corso...</p>
        </div>
      </div>
    );

  if (success)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-md mx-auto px-5 py-24 text-center">
          <PartyPopper className="h-14 w-14 mx-auto text-accent mb-4" strokeWidth={1.5} />
          <h1 className="font-serif text-4xl font-bold mb-2">Sei dentro la Bottega!</h1>
          <p className="text-muted-foreground mb-8">
            Hai sbloccato tutti i livelli, le lectiones e le Prove. Buono studio!
          </p>
          <Button className="rounded-sm bg-primary hover:bg-primary/90" data-testid="go-to-dashboard-btn"
            onClick={() => navigate("/")}>
            Vai in Bottega
          </Button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/")} data-testid="sub-back-btn"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Bottega
        </button>

        <div className="text-center max-w-xl mx-auto mb-6">
          <Badge className="rounded-full bg-accent/15 text-accent-foreground border border-accent/30 mb-4">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {trialDays} giorni di prova gratuita
          </Badge>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold mb-3">Prendi il tuo posto in Bottega</h1>
          <p className="text-muted-foreground">
            Sblocca tutti i livelli, impara dai Maestri, affronta le Prove e fai crescere la tua Maestria.
          </p>
        </div>

        <p data-testid="legal-disclaimer-subscribe"
          className="text-sm text-muted-foreground bg-secondary/60 border border-border rounded-md p-4 max-w-3xl mx-auto mb-8 leading-relaxed">
          {LEGAL_TEXT}
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto items-start">
          {plans.map((p) => {
            const featured = p.id === "annuale";
            const mensilePlan = plans.find((x) => x.id === "mensile");
            const mensileAmount = mensilePlan ? mensilePlan.amount : null;
            let subline = null;
            if (p.id === "annuale") {
              const perMese = Math.floor((p.amount / 12) * 10) / 10;
              const saving = mensileAmount ? Math.floor((1 - p.amount / (mensileAmount * 12)) * 100) : null;
              subline = `come €${perMese.toFixed(2).replace(".", ",")} al mese${saving != null ? ` — risparmi il ${saving}%` : ""}`;
            } else if (p.id === "mecenate") {
              subline = "sostieni il progetto";
            }
            return (
              <div key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`relative bg-card border rounded-md p-6 flex flex-col ${featured ? "border-primary shadow-[0_12px_40px_-16px_rgba(114,47,55,0.35)] lg:-translate-y-2" : "border-border"}`}>
                {featured && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent text-accent-foreground">
                    <Crown className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Consigliato
                  </Badge>
                )}
                {p.id === "mecenate" && (
                  <Badge variant="outline" className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full border-accent/50 bg-card text-accent-foreground">
                    <Crown className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Mecenate
                  </Badge>
                )}
                <h3 className="font-serif text-2xl font-bold mb-1">{p.nome}</h3>
                <div className="flex items-baseline gap-1">
                  <span className="font-serif text-4xl font-bold">€{p.amount.toFixed(2).replace(".", ",").replace(",00", "")}</span>
                  <span className="text-muted-foreground text-sm">/ {p.periodo}</span>
                </div>
                <p className="text-sm text-accent-foreground/70 mb-4 min-h-[20px]">{subline}</p>

                <ul className="space-y-2.5 mb-7 flex-1">
                  {BENEFITS.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm">
                      <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" strokeWidth={2} />
                      <span className="text-muted-foreground">{b}</span>
                    </li>
                  ))}
                  {p.id === "mecenate" && (
                    <>
                      <li className="flex items-start gap-2.5 text-sm">
                        <Crown className="h-4 w-4 text-accent shrink-0 mt-0.5" strokeWidth={2} />
                        <span className="text-foreground font-medium">Badge Mecenate della Bottega</span>
                      </li>
                      <li className="flex items-start gap-2.5 text-sm">
                        <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" strokeWidth={2} />
                        <span className="text-muted-foreground">In futuro: accesso anticipato ai nuovi percorsi</span>
                      </li>
                    </>
                  )}
                </ul>

                <Button
                  data-testid={`subscribe-btn-${p.id}`}
                  onClick={() => subscribe(p.id)}
                  disabled={loadingPlan !== null}
                  className={`w-full rounded-sm h-11 ${featured ? "bg-primary hover:bg-primary/90" : "bg-foreground hover:bg-foreground/90 text-background"}`}>
                  {loadingPlan === p.id && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {p.id === "mecenate" ? "Diventa Mecenate" : `Entra in Bottega · ${p.nome}`}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8 max-w-md mx-auto">
          Pagamento sicuro tramite Stripe. L'accesso viene attivato immediatamente dopo il pagamento.
          Il rinnovo è manuale: al termine del periodo potrai riabbonarti quando vuoi.
        </p>
      </div>
    </div>
  );
}
